import React, { useState } from 'react';
import { Heart, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function Footer() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const privacyContent = {
    title: 'Política de Privacidad',
    lastUpdated: 'Enero 2026',
    sections: [{
      title: 'Información que Recopilamos',
      content: 'Recopilamos información que nos proporcionas directamente al comprar nuestro producto digital, incluyendo nombre, correo electrónico e información de pago procesada de forma segura a través de nuestro procesador de pagos.'
    }, {
      title: 'Uso de la Información',
      content: 'Utilizamos tu información para procesar tu compra, enviarte el producto digital adquirido, y comunicarnos contigo sobre tu pedido. No compartimos tu información personal con terceros excepto cuando es necesario para procesar tu pago.'
    }, {
      title: 'Seguridad de Datos',
      content: 'Implementamos medidas de seguridad para proteger tu información personal. Todos los pagos se procesan a través de plataformas seguras y encriptadas.'
    }, {
      title: 'Tus Derechos',
      content: 'Tienes derecho a acceder, corregir o eliminar tu información personal en cualquier momento. Puedes contactarnos para ejercer estos derechos.'
    }, {
      title: 'Cookies',
      content: 'Utilizamos cookies esenciales para el funcionamiento del sitio web y para mejorar tu experiencia de usuario.'
    }]
  };
  const termsContent = {
    title: 'Términos y Condiciones',
    lastUpdated: 'Enero 2026',
    sections: [{
      title: 'Aceptación de Términos',
      content: "Al comprar 'El Milagro del Sobre', aceptas estos términos y condiciones. Si no estás de acuerdo, por favor no realices la compra."
    }, {
      title: 'Producto Digital',
      content: 'El Milagro del Sobre es un producto digital (e-book) que se entrega inmediatamente después de la compra. Recibirás acceso al contenido digital a través del correo electrónico proporcionado.'
    }, {
      title: 'Licencia de Uso',
      content: 'Al comprar este producto, obtienes una licencia de uso personal y no transferible. No está permitido compartir, revender o distribuir el contenido.'
    }, {
      title: 'Política de Reembolso',
      content: 'Debido a la naturaleza digital del producto y el acceso inmediato al contenido, no ofrecemos reembolsos después de la compra. Te recomendamos revisar toda la información del producto antes de comprar.'
    }, {
      title: 'Propiedad Intelectual',
      content: "Todo el contenido de 'El Milagro del Sobre' está protegido por derechos de autor. Queda prohibida la reproducción, distribución o uso comercial sin autorización expresa."
    }, {
      title: 'Limitación de Responsabilidad',
      content: 'El contenido se proporciona con fines educativos e informativos. Los resultados pueden variar según cada persona. No garantizamos resultados financieros específicos.'
    }]
  };
  const Modal = ({
    isOpen,
    onClose,
    content
  }: {
    isOpen: boolean;
    onClose: () => void;
    content: typeof privacyContent;
  }) => <AnimatePresence data-id="element-54">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-55" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-56">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" data-id="element-57">
              {/* Header */}
              <div className="bg-aged-green p-6 flex justify-between items-center" data-id="element-58">
                <div data-id="element-59">
                  <h2 className="text-2xl font-bold text-[#f4c430]" data-id="element-60">
                    {content.title}
                  </h2>
                  <p className="text-white/80 text-sm mt-1" data-id="element-61">
                    Última actualización: {content.lastUpdated}
                  </p>
                </div>
                <button onClick={onClose} className="text-white hover:text-[#f4c430] transition-colors p-2" data-id="element-62">
                  <X size={24} data-id="element-63" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]" data-id="element-64">
                <div className="space-y-6" data-id="element-65">
                  {content.sections.map((section, index) => <div key={index} data-id="element-66">
                      <h3 className="text-lg font-bold text-[#1a3d3d] mb-2" data-id="element-67">
                        {section.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed" data-id="element-68">
                        {section.content}
                      </p>
                    </div>)}
                </div>
              </div>

              {/* Footer */}
              <div className="bg-aged-cream p-4 border-t border-[#e8dcc8]" data-id="element-69">
                <button onClick={onClose} className="w-full bg-[#1a3d3d] text-white py-3 rounded-lg font-bold hover:bg-[#2d4a3a] transition-colors" data-id="element-70">
                  Cerrar
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
  return <>
      <footer className="bg-aged-cream py-12 border-t border-[#e8dcc8] relative overflow-hidden" data-id="element-71">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-yellow-400/10 rounded-full blur-3xl" data-id="element-72" />

        <div className="container mx-auto px-4 relative z-10" data-id="element-73">
          <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-74">
            <div className="mb-6 md:mb-0 text-center md:text-left" data-id="element-75">
              <span className="text-2xl font-heading font-bold text-[#1a3d3d]" data-id="element-76">
                El Milagro del Sobre<span className="text-[#f4c430]" data-id="element-77">.</span>
              </span>
              <p className="text-[#2d4a3a] mt-2 text-sm" data-id="element-78">
                Tu relación con el dinero puede ser distinta.
              </p>
            </div>

            <div className="flex gap-8 text-sm text-[#2d4a3a]" data-id="element-79">
              <button onClick={() => setShowTerms(true)} className="hover:text-[#1a3d3d] transition-colors font-medium" data-id="element-80">
                Términos
              </button>
              <button onClick={() => setShowPrivacy(true)} className="hover:text-[#1a3d3d] transition-colors font-medium" data-id="element-81">
                Privacidad
              </button>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-[#e8dcc8] text-center text-[#2d4a3a] text-sm flex items-center justify-center gap-2" data-id="element-82">
            <p data-id="element-83">Hecho con</p>
            <Heart size={14} className="text-[#f4c430] fill-[#f4c430]" data-id="element-84" />
            <p data-id="element-85">para latinas.</p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <Modal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} content={privacyContent} data-id="element-86" />
      <Modal isOpen={showTerms} onClose={() => setShowTerms(false)} content={termsContent} data-id="element-87" />
    </>;
}